import { useEffect, useState } from 'react'
import { chargerEvenementsCourse, urlPhotoPreuve } from '../api/operations'
import { ecartLivraison, formaterDistance, SEUIL_LITIGE_M } from '../api/geo'
import { cibleAppel, lienAppel } from '../api/destinataire'
import { cibleWhatsApp, preparerWhatsApp } from '../api/whatsapp'
import { CANAUX, STATUT_DEPUIS_BASE, STATUTS, type Course, type DonneesOps, type StatutBase } from '../api/types'
import { dateCourte, fcfa, heure } from './Format'
import { Icone } from './Icone'
import { Modale } from './Modale'
import { BilanPrix } from './BilanPrix'
import { DemandePosition } from './DemandePosition'
import { SectionEncaissement } from './Encaissement'
import { LIBELLE_VEHICULE, type DetailPrix } from '../api/prixOpere'
import { useDroits } from './Contexte'
import { decoderTrace, dureeLisible, trajetRoute, type TraceCarte, type Trajet } from '../api/itineraire'

/**
 * Détail d'une course — et, si elle est livrée, sa PREUVE (migration 0021).
 *
 * La photo est dans le bucket PRIVÉ `preuves` : on demande une URL signée de
 * 5 minutes au moment d'ouvrir le détail, jamais un lien public.
 */
export function DetailCourse({
  course,
  donnees,
  surFermer,
  surNotifier,
  surRafraichir,
  surTracer,
  surEcrireClient,
}: {
  course: Course
  donnees: DonneesOps
  surFermer: () => void
  /** Dessine l'itinéraire de la course sur la carte de l'écran appelant (0066). Absent = pas de bouton. */
  surTracer?: (t: TraceCarte) => void
  /** Ouvre la messagerie sur le client de la course (0067). Absent = pas de bouton. */
  surEcrireClient?: (clientId: string, courseId: string) => void
  surNotifier?: (message: string) => void
  /** Absent = la section « Encaissement » est lecture seule (pas de bouton) : l'écran appelant ne sait pas recharger. */
  surRafraichir?: () => Promise<void>
}) {
  const { apercu } = useDroits()
  const client = donnees.clients.find((c) => c.id === course.client_account_id)
  const livreur = donnees.drivers.find((d) => d.id === course.driver_id)
  const [photo, setPhoto] = useState<{ etat: 'aucune' | 'chargement' | 'ok' | 'erreur'; url?: string; message?: string }>(
    course.proof_photo_path ? { etat: 'chargement' } : { etat: 'aucune' },
  )
  const [evenements, setEvenements] = useState<{ status: StatutBase; note: string | null; at: string }[] | null>(null)

  useEffect(() => {
    let vivant = true
    if (course.proof_photo_path && !apercu) {
      urlPhotoPreuve(course.proof_photo_path)
        .then((url) => vivant && setPhoto({ etat: 'ok', url }))
        .catch((e: unknown) => vivant && setPhoto({ etat: 'erreur', message: e instanceof Error ? e.message : String(e) }))
    } else if (course.proof_photo_path && apercu) {
      setPhoto({ etat: 'erreur', message: 'Aperçu de développement : la photo réelle n’est pas chargée.' })
    }
    if (!apercu) {
      chargerEvenementsCourse(course.id)
        .then((e) => vivant && setEvenements(e))
        .catch(() => vivant && setEvenements([]))
    } else setEvenements([])
    return () => {
      vivant = false
    }
  }, [course.id, course.proof_photo_path, apercu])

  const ecart = ecartLivraison(course)
  const statut = STATUTS[course.statut]
  /* Mise à jour WhatsApp : lien `wa.me` avec un message prérempli selon le statut — la
     personne relit et envoie elle-même, rien ne part seul. Le numéro est celui du
     DESTINATAIRE s'il existe (0028), sinon celui du client ; son nom, s'il est connu, ouvre
     le message. Même règle que l'envoi automatique. */
  const cible = cibleWhatsApp(course, [client?.phone])
  const whatsapp = preparerWhatsApp(course.statut, cible.telephones, {
    ref: course.ref,
    marque: donnees.org.brand_name || donnees.org.name,
    codeRemise: course.delivery_code,
    nom: cible.nom,
  })
  /* Bouton d'appel : le destinataire en priorité, sinon le client (téléphone de la course, puis de la fiche). */
  const aJoindre = cibleAppel({ ...course, client_phone: course.client_phone ?? client?.phone ?? null })
  const lienTel = lienAppel(aJoindre.telephone)

  return (
    <Modale titre={`Course ${course.ref}`} ouverte surFermer={surFermer}>
      <div className="preuve" style={{ marginBottom: 14 }}>
        <div>
          <div className="t-muted">Client</div>
          <div className="t-strong">{client?.name ?? '—'}</div>
        </div>
        <div>
          <div className="t-muted">Destinataire</div>
          {course.recipient_name || course.recipient_phone ? (
            <>
              <div className={course.recipient_name ? 't-strong' : 't-muted'}>{course.recipient_name ?? 'Nom non renseigné'}</div>
              <div className={course.recipient_phone ? 'mono' : 't-muted'}>{course.recipient_phone ?? 'Pas de téléphone : on joint le client'}</div>
            </>
          ) : (
            <div className="t-muted">Le client reçoit lui-même</div>
          )}
        </div>
        <div>
          <div className="t-muted">Statut</div>
          <span className={`badge ${statut.pastille}`}>{statut.libelle}</span>
        </div>
        <div>
          <div className="t-muted">Trajet</div>
          <div>
            {course.pickup} → {course.dropoff}
          </div>
        </div>
        <div>
          <div className="t-muted">Prix</div>
          <div className="mono">
            {fcfa(course.price_fcfa)}
            {course.surcharge_fcfa > 0 && <span className="t-muted"> (dont {fcfa(course.surcharge_fcfa)} hors créneau)</span>}
          </div>
        </div>
        <div>
          <div className="t-muted">Livreur</div>
          <div>{livreur?.full_name ?? '—'}</div>
        </div>
        <div>
          <div className="t-muted">Canal · créée</div>
          <div>
            {CANAUX[course.channel]?.libelle ?? '—'} · {dateCourte(course.created_at)} {heure(course.created_at)}
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {surEcrireClient && client && (
          <button className="btn btn-outline btn-sm" onClick={() => surEcrireClient(client.id, course.id)}
            title={client.a_un_compte ? 'Le client lit la réponse dans l’app' : 'Sans compte : la messagerie propose WhatsApp'}>
            <Icone nom="chat" /> Écrire au client
          </button>
        )}
        {lienTel ? (
          <a className="btn btn-outline btn-sm" href={lienTel}>
            <Icone nom="phone" /> Appeler {aJoindre.destinataire ? 'le destinataire' : 'le client'}
          </a>
        ) : (
          <button className="btn btn-outline btn-sm" disabled title="Aucun numéro enregistré pour cette course">
            <Icone nom="phone" /> Appeler
          </button>
        )}
      </div>

      <h4 style={{ margin: '4px 0 8px' }}>Position de livraison</h4>
      <div style={{ marginBottom: 14 }}>
        <DemandePosition
          course={course}
          telephoneClient={client?.phone ?? null}
          marque={donnees.org.brand_name || donnees.org.name}
          {...(surNotifier ? { surNotifier } : {})}
        />
      </div>

      {surTracer && course.pickup_lat != null && course.pickup_lng != null && course.dropoff_lat != null && course.dropoff_lng != null && (
        <ItineraireCourse course={course} surTracer={surTracer} />
      )}

      <h4 style={{ margin: '4px 0 8px' }}>Prix</h4>
      <div style={{ marginBottom: 14 }}>
        {course.price_breakdown?.source === 'grille' && course.price_breakdown.base !== undefined ? (
          <BilanPrix detail={course.price_breakdown as DetailPrix} titreTotal="Prix calculé par la grille" />
        ) : course.price_breakdown?.source === 'override' ? (
          <div className="bandeau alerte" style={{ marginBottom: 0 }}>
            <strong>Prix dérogé par le propriétaire</strong> : {fcfa(course.price_fcfa)}
            {course.price_breakdown.total !== undefined && <> (la grille donnait {fcfa(course.price_breakdown.total)})</>}.
            <div>Motif : {course.price_override_reason ?? course.price_breakdown.raison ?? '—'}</div>
          </div>
        ) : course.price_breakdown?.source === 'saisie' ? (
          <div className="bandeau alerte" style={{ marginBottom: 0 }}>
            <strong>Prix saisi à la main</strong> : la course n’avait pas de coordonnées au départ et la base n’a pas pu le calculer.
            Dès que les deux points sont connus (et tant que la course n’est pas assignée), le prix est recalculé.
          </div>
        ) : (
          <div className="t-muted">Prix enregistré avant la grille tarifaire : {fcfa(course.price_fcfa)}.</div>
        )}
        {course.requested_vehicle && (
          <div className="t-muted" style={{ marginTop: 6, fontSize: 12 }}>
            Véhicule demandé : {LIBELLE_VEHICULE[course.requested_vehicle]}
            {course.distance_km != null ? ` · distance retenue ${course.distance_km.toFixed(2).replace('.', ',')} km` : ''}
          </div>
        )}
      </div>

      {surRafraichir && surNotifier && (
        <SectionEncaissement course={course} donnees={donnees} surRafraichir={surRafraichir} surNotifier={surNotifier} />
      )}

      <div style={{ marginBottom: 14 }}>
        {whatsapp.ok ? (
          <a className="btn btn-green btn-sm" href={whatsapp.url} target="_blank" rel="noopener noreferrer">
            <Icone nom="chat" /> Prévenir le destinataire sur WhatsApp
          </a>
        ) : (
          <>
            <button className="btn btn-green btn-sm" disabled>
              <Icone nom="chat" /> Prévenir le destinataire sur WhatsApp
            </button>
            <div className="t-muted" style={{ marginTop: 6, fontSize: 12 }}>
              {whatsapp.raison}
            </div>
          </>
        )}
        {whatsapp.ok && (
          <div className="t-muted" style={{ marginTop: 6, fontSize: 12 }}>
            Ouvre WhatsApp avec un message prêt pour le statut « {statut.libelle} » : vous le relisez, puis vous l’envoyez.
          </div>
        )}
      </div>

      {course.statut === 'livree' && (
        <>
          <h4 style={{ margin: '4px 0 10px' }}>Preuve de livraison</h4>
          <div className="preuve">
            <div>
              <div className="t-muted">Livrée le</div>
              <div>
                {dateCourte(course.delivered_at)} {heure(course.delivered_at)}
              </div>
            </div>
            <div>
              <div className="t-muted">Réceptionnée par</div>
              <div className={course.proof_recipient_name ? 't-strong' : 't-muted'}>
                {course.proof_recipient_name ?? 'Nom non saisi'}
              </div>
            </div>
            <div>
              <div className="t-muted">Écart validation ↔ point de livraison</div>
              <div>
                {ecart.etat === 'mesure' ? (
                  ecart.litige ? (
                    <span className="ecart-litige">
                      {formaterDistance(ecart.metres)} — au-delà de {SEUIL_LITIGE_M} m : signal de litige
                    </span>
                  ) : (
                    <span className="ecart-ok">{formaterDistance(ecart.metres)} — cohérent</span>
                  )
                ) : ecart.etat === 'sans_point_livraison' ? (
                  <span className="t-muted">Non mesurable : le point de livraison n’a pas été géolocalisé (saisie par quartier).</span>
                ) : (
                  <span className="t-muted">Non mesurable : le livreur n’a pas transmis sa position en validant.</span>
                )}
              </div>
            </div>
            <div>
              <div className="t-muted">Code de remise</div>
              <div className={course.delivery_code ? 'mono' : 't-muted'}>
                {course.delivery_code ? `${course.delivery_code} (vérifié à la remise)` : 'Aucun code pour cette course'}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            {photo.etat === 'aucune' && (
              <div className="bandeau">
                Aucune photo de remise.
                {donnees.org.require_delivery_proof
                  ? ' La photo est exigée aujourd’hui ; cette course a été close avant ce réglage ou sans lui.'
                  : ' La photo n’est pas exigée par votre organisation (réglage dans « Livreurs »).'}
              </div>
            )}
            {photo.etat === 'chargement' && <div className="t-muted">Chargement de la photo…</div>}
            {photo.etat === 'erreur' && <div className="bandeau erreur">Photo indisponible : {photo.message}</div>}
            {photo.etat === 'ok' && photo.url && (
              <a href={photo.url} target="_blank" rel="noopener noreferrer">
                <img className="preuve-photo" src={photo.url} alt={`Photo de remise de la course ${course.ref}`} />
              </a>
            )}
          </div>
        </>
      )}

      {evenements && evenements.length > 0 && (
        <>
          <h4 style={{ margin: '16px 0 8px' }}>Historique</h4>
          <ul className="timeline">
            {evenements.map((e, i) => (
              <li key={i}>
                <span className="t-strong">{STATUTS[STATUT_DEPUIS_BASE[e.status] ?? 'creee'].libelle}</span>{' '}
                <span className="t-muted">
                  {dateCourte(e.at)} {heure(e.at)}
                  {e.note ? ` · ${e.note}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Modale>
  )
}

/** La route à moto de la collecte à la livraison : distance, durée, et tracé sur la carte. Simple lecture : rien n'est mémorisé ni facturé. */
function ItineraireCourse({ course, surTracer }: { course: Course; surTracer: (t: TraceCarte) => void }) {
  const [etat, setEtat] = useState<{ statut: 'repos' | 'chargement' } | { statut: 'ok'; t: Trajet }>({ statut: 'repos' })
  const de = { lat: course.pickup_lat as number, lng: course.pickup_lng as number }
  const a = { lat: course.dropoff_lat as number, lng: course.dropoff_lng as number }
  async function tracer() {
    setEtat({ statut: 'chargement' })
    const t = await trajetRoute(de, a)
    setEtat({ statut: 'ok', t })
    surTracer({
      titre: `Itinéraire ${course.ref}`,
      points: t.trace ? decoderTrace(t.trace, t.trace_precision) : [[de.lat, de.lng], [a.lat, a.lng]],
      arrets: [
        { ...de, numero: 1, type: 'collecte', libelle: `Collecte ${course.ref}` },
        { ...a, numero: 2, type: 'livraison', libelle: `Livraison ${course.ref}` },
      ],
      depart: null,
    })
  }
  return (
    <>
      <h4 style={{ margin: '4px 0 8px' }}>Itinéraire</h4>
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button className="btn btn-outline btn-sm" disabled={etat.statut === 'chargement'} onClick={() => void tracer()}>
          <Icone nom="pin" /> {etat.statut === 'chargement' ? 'Calcul…' : 'Tracer sur la carte'}
        </button>
        {etat.statut === 'ok' && (
          <span className="t-muted" style={{ fontSize: 13 }}>
            {etat.t.source === 'valhalla'
              ? `${String(etat.t.distance_km).replace('.', ',')} km par la route${etat.t.duree_s ? `, environ ${dureeLisible(etat.t.duree_s)} à moto` : ''}`
              : `Itinéraire indisponible : ${String(etat.t.distance_km).replace('.', ',')} km estimés, tracé à vol d’oiseau`}
          </span>
        )}
      </div>
    </>
  )
}
